/* eslint-disable no-extra-boolean-cast */
/* eslint-disable no-inner-declarations */
import { HeadersFunction, LoaderFunction, MetaFunction } from "react-router";
import { redirect, useLoaderData, useNavigate, useSearchParams } from "react-router";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import DailyDarshanPhotos from "~/components/daily-darshan/DailyDarshanPhotos";
import DailydarshanSelectedDate from "~/components/daily-darshan/DailydarshanSelectedDate";
import MandirTabs from "~/components/daily-darshan/MandirTabs";
import BreadCrubComponent from "~/components/shared-components/BreadCrubComponent";
import NodataFound from "~/components/shared-components/NoDataFound";
import useDisplayStickyButton from "~/hook/useDisplayStickyButton";
import { useLanguageParams } from "~/hook/useLanguageParams";
import useLocale from "~/hook/useLocale";
import { DailyDarshanLoaderdata, DarshanImage, GroupedImagesByDate } from "~/interface/daily-darshan/daily-darshan";
import { getPageMeta } from "~/lib/helperFuncation/metaHelper";
import { cn } from "~/lib/utils";
import { CACHED_PAGE, FALLBACK_IMAGE_URL, LANG, MandirList } from "~/utils/constant";
import { computeEtagFromData, homeRouteString } from "~/utils/helper-funcations";
import { metaService } from "~/utils/metaService";
import { ApiService } from "~/utils/service";

export const loader: LoaderFunction = async ({ request, params }: any) => {
  const { lang, mandir } = params;
  try {
    const url = new URL(request.url);
    const selectedMandir = mandir || "karelibaug"; // Default to 'karelibaug' if no mandir is provided
    const apiService = new ApiService(lang || LANG.english);

    //get selected date from url
    const date = dayjs(new Date()).format("YYYY-MM-DD");
    const selectedDate = url.searchParams.get("date") !== null ? url.searchParams.get("date") : date; // e.g., "shoes"

    // If no specific location parameter is provided, redirect to karelibuag
    const path = `/${lang}/daily-darshan/${selectedMandir}`;

    if (url.pathname === "/daily-darshan" || url.pathname === "/daily-darshan/") {
      return redirect(`${path}?date=${selectedDate}`);
    } else if (url.searchParams.get("date") === null) {
      return redirect(`${path}?date=${selectedDate}`);
    }

    //get data from daily-darshan api
    const response = await apiService.getDailyDarshanData(selectedMandir, {
      start_date: selectedDate ?? dayjs(new Date()).format("YYYY-MM-DD"),
      n_days: 4,
    });

    const DAILY_DARSHAN = response || [];

    const dailyDarshanData = groupImagesByDate(DAILY_DARSHAN);

    function groupImagesByDate(images: DarshanImage[]): GroupedImagesByDate {
      // Group images by date
      const grouped: GroupedImagesByDate = images.reduce((acc, image) => {
        if (!acc[image.date]) {
          acc[image.date] = [];
        }
        acc[image.date].push(image);
        return acc;
      }, {} as GroupedImagesByDate);

      // Sort the dates in descending order
      const sortedDates = Object.keys(grouped).sort((a, b) => (a < b ? 1 : -1));

      // Create a new object with sorted date keys
      const sortedGrouped: GroupedImagesByDate = {};
      for (const date of sortedDates) {
        sortedGrouped[date] = grouped[date];
      }

      return sortedGrouped;
    }

    const availableDataDatesArray = await apiService.getAvailableDataDatesArray(selectedMandir);

    const dateArray = Object.keys(dailyDarshanData);
    const selectedMandirName = MandirList.find((m) => m.value === selectedMandir);
    const title = response
      ? lang === "gu"
        ? `દૈનિક દર્શન | ${selectedMandirName?.gu}`
        : `Daily Darshan | ${selectedMandirName?.en}`
      : lang === "gu"
        ? `દૈનિક દર્શન`
        : "Daily Darshan";

    const DATA = {
      dailyDarshanData: dailyDarshanData || [],
      title,
      selectedDate,
      selectedMandir: selectedMandir,
      dateArray: dateArray || [],
      availableDataDatesArray,
    };

    const etag = computeEtagFromData(DATA);
    const ifNoneMatch = request.headers.get("If-None-Match");
    if (ifNoneMatch === etag) {
      return new Response(null, {
        status: 304,
        headers: {
          Etag: etag,
        },
      });
    }

    return Response.json(DATA, {
      headers: { ...CACHED_PAGE.DAILY_DARSHAN.HEADERS, ETag: etag },
    });
  } catch (error) {
    console.error("Error in DailyDarshanComponent loader:", error);
  }
};

export const headers: HeadersFunction = ({ loaderHeaders }) => loaderHeaders;

export const handle = {
  breadcrumb: (match: any) => {
    const lang = match.params.lang;
    return (
      <BreadCrubComponent
        breadCrub={[
          {
            path: homeRouteString(lang).path,
            title: homeRouteString(lang).home,
          },
        ]}
      />
    );
  },
};

export const meta: MetaFunction = ({ location, data, params }) => {
  const pathname = location?.pathname || "/";
  const search = location?.search || "";
  const dailyDarshan = data as DailyDarshanLoaderdata;
  const lang = params.lang || "en";
  const meta = getPageMeta("dailyDarshan", lang);
  return metaService({
    title: `${dailyDarshan.title}` || meta.metaTitle,
    description: meta.metaDescription,
    ogTitle: `${dailyDarshan.title}` || meta.ogTitle,
    ogDescription: meta.ogDescription,
    ogImg: meta.ogImage || FALLBACK_IMAGE_URL,
    ogWidth: meta.ogImageWidth || "1200",
    ogHeight: meta.ogImageHeight || "630",
    ogUrl: pathname,
    canonicalLink: pathname + search,
    hreflangs: [
      { hrefLang: "x-default", href: `${pathname}` },
      { hrefLang: "en", href: `${pathname}` },
      { hrefLang: "gu", href: `${pathname}` },
    ],
  });
};

const DailyDarshan = () => {
  const { dailyDarshanData, mandirArray, selectedMandir, selectedDate, dateArray, availableDataDatesArray } = useLoaderData<DailyDarshanLoaderdata>();
  const lang = useLanguageParams();
  const { t } = useTranslation();
  const { showTopButton } = useDisplayStickyButton();

  const [date, setDate] = useState(dayjs(selectedDate).format("YYYY-MM-DD"));
  const navigation = useNavigate();
  const { navigate } = useLocale();
  const [searchParams, setSearchParams] = useSearchParams();
  const apiService = new ApiService(lang || LANG.english);

  // Pagination state
  const [pageIndex, setPageIndex] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(Boolean(dailyDarshanData && Object.keys(dailyDarshanData).length));
  const [allDarshanData, setAllDarshanData] = useState(dailyDarshanData);
  const [allDateArray, setAllDateArray] = useState(dateArray);

  // Reset pagination when mandir or date changes
  useEffect(() => {
    setAllDarshanData(dailyDarshanData);
    setAllDateArray(dateArray);
    setPageIndex(1);
    setHasMore(Boolean(dailyDarshanData && Object.keys(dailyDarshanData).length));
  }, [dailyDarshanData, dateArray, selectedMandir, date]);

  const handleChangeMandir = (mandir: string) => {
    navigate(`/daily-darshan/${mandir}?date=${date}`);
  };

  const handleDateChange = (newDate: any) => {
    const formattedDate = dayjs(newDate).format("YYYY-MM-DD");
    setDate(formattedDate);
    setSearchParams({ date: formattedDate }, { replace: true });
  };

  // Load more handler
  const handleLoadMore = async () => {
    if (loading || !hasMore) return;

    setLoading(true);
    try {
      const lastDate = dayjs(allDateArray[allDateArray.length - 1]).format("YYYY-MM-DD");
      const previousDate = dayjs(lastDate).subtract(1, "day").format("YYYY-MM-DD");
      const response = await apiService.getDailyDarshanData(selectedMandir, {
        start_date: previousDate,
        n_days: 4,
      });

      const newData = response || [];
      if (newData.length === 0) {
        setHasMore(false);
      } else {
        // Group and merge new data
        const groupImagesByDate = (images: DarshanImage[]) => {
          const grouped: Record<string, DarshanImage[]> = images.reduce(
            (acc, image) => {
              if (!acc[image.date]) acc[image.date] = [];
              acc[image.date].push(image);
              return acc;
            },
            {} as Record<string, DarshanImage[]>,
          );
          return grouped;
        };
        const groupedNewData = groupImagesByDate(newData);

        // Merge with previous data
        const mergedData = { ...allDarshanData };
        Object.entries(groupedNewData).forEach(([date, images]) => {
          mergedData[date] = mergedData[date] ? [...mergedData[date], ...images] : images;
        });

        // Update date array (sorted, unique)
        const mergedDates = Array.from(new Set([...allDateArray, ...Object.keys(groupedNewData)])).sort((a, b) => (a < b ? 1 : -1));

        setAllDarshanData(mergedData);
        setAllDateArray(mergedDates);
        setPageIndex(pageIndex + 1);
        setLoading(false);
      }
    } catch (err) {
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  const FilterChip = () => (
    <div className="w-full flex justify-center items-center">
      <MandirTabs mandir={MandirList} setSelectedMandir={handleChangeMandir} selectedMandir={selectedMandir} />
    </div>
  );

  return (
    <>
      <div className="w-full min-h-screen flex flex-col items-center justify-start">
        {/* {showTopButton ? ( */}
        <div className={cn(showTopButton ? "filter-chip-animation" : "filter-chip-non-animation")}>
          {/* <FilterChip /> */}
          <div className="w-full flex justify-center items-center">
            <MandirTabs mandir={MandirList} setSelectedMandir={handleChangeMandir} selectedMandir={selectedMandir} />
          </div>
        </div>
        {/* ) : (
          <div className="filter-chip-non-animation ">
            <FilterChip />
          </div>
        )} */}
        <div className="flex justify-center items-center">
          <DailydarshanSelectedDate setDate={handleDateChange} selectedDate={date} availableDataDatesArray={availableDataDatesArray} />
        </div>
        <div className="w-full flex justify-start items-center p-3 overflow-hidden">
          {Boolean(allDarshanData && Object.keys(dailyDarshanData).length) ? (
            <DailyDarshanPhotos
              mandirImages={allDarshanData}
              dateArray={allDateArray}
              onLoadMore={handleLoadMore}
              hasMore={hasMore}
              loading={loading}
              selectedDham={selectedMandir}
            />
          ) : (
            <div className="w-full flex justify-center items-center mt-40">
              <NodataFound text={t("daily-darshan.no_data_for_date")} />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export function ErrorBoundary({ error }: { error: Error }) {
  // console.error("Error in dailydarshan component", error);
  return <NodataFound text={"No Images found for this date"} />;
}

export default DailyDarshan;
